import { CharacterCount } from "@tiptap/extension-character-count";
import { Color } from "@tiptap/extension-color";
import { Link } from "@tiptap/extension-link";
import { Mention } from "@tiptap/extension-mention";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import {
	generateHTML,
	type JSONContent,
	mergeAttributes,
	ReactNodeViewRenderer,
	useEditor,
} from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { useMemo } from "react";
import { localized } from "../../localization";
import { MentionItem } from "./mention-item";
import type { MentionType } from "./mention-list";
import { buildMentionExtension, type TextareaProps } from "./mentions";
import { buildImageBlock } from "./image-block";
import { buildVideoBlock } from "./video-block";

/** Props Interface */
interface UseTiptapProps extends TextareaProps {
	isReadOnly: boolean;
	isDisabled?: boolean;
	value: string | JSONContent;
	onChange: (value: string) => void;
	dataCy?: string;
	placeholder?: string;
}

/** Extensions */
const _extensions = [
	StarterKit,
	Link,
	TextAlign.configure({
		types: ["heading", "paragraph"],
	}),
	Underline,
	CharacterCount.configure({}),
	Color,
	TextStyle,
	Table.configure({
		resizable: true,
	}),
	TableRow,
	TableHeader,
	TableCell,
];

export function useTiptap({
	value,
	members,
	projects,
	mentionRenderers,
	videoRenderer,
	imageRenderer,
	onChange,
	isReadOnly,
	placeholder,
	isDisabled = false,
	...props
}: UseTiptapProps) {
	const extensions = useMemo(() => {
		return [
			..._extensions,
			// In the list unconditionally: `generateHTML` throws on unknown node types, and the
			// catch below falls back to the raw JSON string in every read-only view.
			buildVideoBlock(videoRenderer),
			buildImageBlock(imageRenderer),
			Placeholder.configure({
				emptyEditorClass:
					"cursor-text before:content-[attr(data-placeholder)] before:absolute before:top-0 before:left-0 before:text-mauve-11 before:opacity-50 before-pointer-events-none",
				placeholder: placeholder || localized("notes.add_note"),
			}),
			Mention.configure({
				HTMLAttributes: {
					class: "mention",
				},
				suggestion: buildMentionExtension({
					members,
					mentionRenderers,
					projects,
				}),
			}).extend({
				addAttributes() {
					return {
						avatar: {
							default: null,
						},
						id: {
							default: null,
						},
						label: {
							default: null,
						},
						type: {
							default: null,
						},
					};
				},
				addNodeView() {
					return ReactNodeViewRenderer((props) => (
						<MentionItem
							{...props}
							mentionRenderers={mentionRenderers}
						/>
					));
				},

				parseHTML() {
					return [
						{
							getAttrs: (element) => {
								const id = element.getAttribute("data-mention-id");
								const type = element.getAttribute("data-type") as MentionType;
								const label = element.textContent?.startsWith("@")
									? element.textContent.substring(1)
									: element.textContent;

								return {
									id,
									label,
									type,
								};
							},
							// Look for a <span> tag that has both 'data-mention-id' AND 'data-type' attributes
							tag: "mention-component[data-mention-id][data-type]",
						},
					];
				},

				renderHTML({ node, HTMLAttributes }) {
					return [
						"mention-component",
						mergeAttributes(
							{
								class: `mention mention-${node.attrs.type}`,
								"data-mention-id": node.attrs.id,
								"data-type": node.attrs.type,
							},
							HTMLAttributes,
						),
						`@${node.attrs.label}`,
					];
				},
			}),
		];
	}, [members, projects, mentionRenderers, videoRenderer, imageRenderer]);

	/** Functions */
	function handleOnGenerateContent(value: string | JSONContent) {
		try {
			const json = JSON.parse(value as string);
			const generated = generateHTML(json, extensions);
			return generated;
		} catch {
			return value;
		}
	}

	/** Memoized */
	const generatedHTML = useMemo(() => {
		const content = handleOnGenerateContent(value);
		return content;
	}, [value, isReadOnly, extensions]);

	/** Tiptap */
	const editor = useEditor({
		content: generatedHTML,
		editable: !isReadOnly && !isDisabled,
		extensions,
		editorProps: {
			attributes: {
				"data-cy": props.dataCy || "tiptap-editor",
			},
		},
		coreExtensionOptions: {
			clipboardTextSerializer: {
				blockSeparator: "\n",
			},
		},
		onUpdate: ({ editor }) => {
			if (editor.isEmpty) {
				onChange("");
			} else {
				onChange(JSON.stringify(editor.getJSON()));
			}
		},
	});

	return { editor, handleOnGenerateContent };
}
